import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, Palette } from "lucide-react";

const COLOR_THEMES = [
  { id: "red",      name: "Inferno Red",   hex: "#E11D2B", note: "Default brand · Repco-inspired" },
  { id: "logored",  name: "Badge Red",     hex: "#D60B12", note: "Exact match to your logo's red" },
  { id: "scarlet",  name: "Scarlet Racing", hex: "#EF2D3C", note: "Brighter track-day take on the logo red" },
  { id: "gunmetal", name: "Chrome Gunmetal", hex: "#64748B", note: "The silver of your logo's wrenches" },
  { id: "gold",     name: "Racing Gold",   hex: "#C28E0E", note: "Classic black + gold pit-crew pairing" },
  { id: "ember",    name: "Rust Ember",    hex: "#D9481F", note: "Warm exhaust-glow companion to the badge" },
  { id: "crimson",  name: "Crimson Oxblood", hex: "#9F1239", note: "Deeper, classic British-racing red" },
  { id: "amber",    name: "Sunset Amber",  hex: "#EA580C", note: "Energetic · workshop-warm" },
  { id: "copper",   name: "Brushed Copper", hex: "#B45309", note: "Metallic · vintage trade feel" },
  { id: "neon",     name: "Acid Neon",     hex: "#84CC16", note: "Electric · racing-circuit punch" },
  { id: "green",    name: "Forest Green",  hex: "#16A34A", note: "Premium · eco-conscious" },
  { id: "blue",     name: "Steel Blue",    hex: "#2563EB", note: "Professional · trustworthy" },
  { id: "midnight", name: "Midnight Indigo", hex: "#4F46E5", note: "After-hours · premium tech" },
  { id: "purple",   name: "Royal Purple",  hex: "#7C3AED", note: "Premium · luxury feel" },
  { id: "carbon",   name: "Carbon Platinum", hex: "#E5E5E5", note: "Monochrome · stealth grayscale" },
];

export default function ThemeManager({ currentTheme, onChange }) {
  const [saving, setSaving] = useState(false);

  const apply = async (id) => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { theme_id: id });
      onChange(id); // optimistic immediate apply
      toast.success(`Theme set to ${COLOR_THEMES.find((t) => t.id === id)?.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 border-l-2 border-brand-red pl-4 mb-2">
        <Palette className="h-5 w-5 text-brand-red mt-0.5" />
        <div>
          <h3 className="font-display uppercase text-xl text-white tracking-tight">Site colour theme</h3>
          <p className="text-sm text-zinc-400 max-w-xl mt-1">Pick the accent colour used across the entire site. Changes apply instantly for you and within seconds for everyone else.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {COLOR_THEMES.map((t) => {
          const active = currentTheme === t.id;
          return (
            <button
              key={t.id}
              data-testid={`theme-${t.id}`}
              disabled={saving || active}
              onClick={() => apply(t.id)}
              className={`group text-left bg-zinc-950 border transition-all overflow-hidden ${active ? "border-transparent ring-2" : "border-zinc-900 hover:border-zinc-700"}`}
              style={active ? { boxShadow: `0 0 0 2px ${t.hex}` } : {}}
            >
              {/* Mini hero preview */}
              <div className="relative h-28 overflow-hidden" style={{ background: `linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, ${t.hex}26 100%)` }}>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8" style={{ backgroundColor: t.hex }} />
                    <div className="font-display uppercase text-lg tracking-tighter text-white">Aa</div>
                    <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest" style={{ backgroundColor: t.hex, color: t.id === "neon" || t.id === "carbon" ? "#0a0a0a" : "#ffffff" }}>
                      Button
                    </div>
                  </div>
                </div>
                {active && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-white" style={{ backgroundColor: t.hex, color: t.id === "neon" || t.id === "carbon" ? "#0a0a0a" : "#ffffff" }}>
                    <Check className="h-3 w-3" /> Active
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display uppercase tracking-tight text-white text-base">{t.name}</div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t.hex}</span>
                </div>
                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{t.note}</p>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Light/dark mode toggle is still controlled by each visitor (auto-switches at sunrise/sunset by their local time).</p>
    </div>
  );
}

